import { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';

import { getPageByPath } from '@lib/wordpress';
import { parseWordPressContent } from '@lib/wordpressContentParser';
import { ServicePage } from '@modules/services/servicePage/ServicePage';

interface ServiceAreaPageProps {
  parsedContent: ReturnType<typeof parseWordPressContent>;
  pageTitle: string;
  pageExcerpt: string;
  query: string;
  baseUrl: string;
  servicePath: string;
}

interface CreateServiceAreaPageConfig {
  servicePath: string; // e.g., 'heating-and-gas', 'plumbing', 'electrical'
  baseUrl: string; // e.g., 'https://www.qeeper.co.uk'
}

/**
 * Creates a dynamic service area page component
 */
export function createServiceAreaPage(config: CreateServiceAreaPageConfig) {
  const { servicePath, baseUrl } = config;

  const ServiceAreaPage = ({
    parsedContent,
    pageTitle,
    pageExcerpt,
    query,
  }: Omit<ServiceAreaPageProps, 'baseUrl' | 'servicePath'>) => {
    const url = `${baseUrl}/services/${servicePath}/${query}`;

    return (
      <>
        <Head>
          <link rel="canonical" key="canonical" href={url} />
          <link rel="alternate" hrefLang="en" href={url} />
        </Head>
        <NextSeo
          title={pageTitle}
          description={pageExcerpt}
          canonical={url}
          openGraph={{
            url: url,
            title: pageTitle,
            description: pageExcerpt,
          }}
        />
        <ServicePage
          coverImageLink={parsedContent.coverImageLink}
          coverImageAlt={parsedContent.coverImageAlt}
          coverImageTitle={parsedContent.coverImageTitle}
          pageTitle={parsedContent.pageTitle}
          pageDescription={parsedContent.pageDescription}
          buttonTitle={parsedContent.buttonTitle}
          galleryTitle={parsedContent.galleryTitle}
          galleryImage1Link={parsedContent.galleryImage1Link}
          galleryImage1Title={parsedContent.galleryImage1Title}
          galleryImage1Description={parsedContent.galleryImage1Description}
          galleryImage2Link={parsedContent.galleryImage2Link}
          galleryImage2Title={parsedContent.galleryImage2Title}
          galleryImage2Description={parsedContent.galleryImage2Description}
          galleryImage3Link={parsedContent.galleryImage3Link}
          galleryImage3Title={parsedContent.galleryImage3Title}
          galleryImage3Description={parsedContent.galleryImage3Description}
          blockTitle={parsedContent.blockTitle}
          blockDescription={parsedContent.blockDescription}
          blockImageLink={parsedContent.blockImageLink}
          faqHeader={parsedContent.faqHeader}
          faqItems={parsedContent.faqItems}
          serviceList={parsedContent.serviceList}
          mainPageUrl={`/services/${servicePath}`}
        />
      </>
    );
  };

  return ServiceAreaPage;
}

/**
 * Creates getStaticPaths for service area pages
 */
export const createServiceAreaStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

/**
 * Creates getStaticProps for service area pages
 */
export function createServiceAreaStaticProps(servicePath: string) {
  const getStaticProps: GetStaticProps = async ({ params }) => {
    const query = params?.query as string;

    if (!query) {
      return {
        notFound: true,
      };
    }

    try {
      const page = await getPageByPath(query, servicePath);

      if (!page) {
        return {
          redirect: {
            destination: `/services/${servicePath}`,
            permanent: false,
          },
        };
      }
      // Parse the WordPress content
      const parsedContent = parseWordPressContent(page.content.rendered);
      return {
        props: {
          parsedContent,
          pageTitle: page.yoast_head_json?.title ?? page.title.rendered,
          pageExcerpt: page.yoast_head_json?.description ?? page.excerpt.rendered.replace(/<[^>]*>/g, ''),
          query,
        },
        revalidate: 600, // Revalidate every 10 minutes
      };
    } catch (error) {
      console.error('Error fetching WordPress page:', error);
      return {
        notFound: true,
      };
    }
  };

  return getStaticProps;
}
